import { action, computed, makeObservable, observable, override } from 'mobx'
import { GROUP_API } from '@voicekey/api/group/group'
import { getURL, URL_TYPES } from '@voicekey/api/http'
import { MEDIASTORAGE_API } from '@voicekey/api/mediaStorage/mediaStorage'
import { COMPARISONS, OPERANDS } from '@voicekey/api/mediaStorage/mediaStorage.const'
import { TRACKER_API } from '@voicekey/api/tracker/tracker'
import { SEVERITIES } from 'store/messagesStore'
import { generateUID } from '@voicekey/libraries/generateUID/generateUID'
import { MODALITIES } from 'App.const'
import dynamicMessages from 'locales/dynamicMessages'
import { ListItemsStore } from './listItemsStore'
import { defaultPage } from '@voicekey/objects/listItemsObject'

const url = getURL(URL_TYPES.TRACKER)

export class PersonsStore extends ListItemsStore {
  constructor (rootStore, ...props) {
    super(rootStore, ...props)
    makeObservable(this, {
      _persons: observable,
      _dataLoadingQueue: observable,
      loadPersonData: action,
      addPersonToDataLoadingQueue: action,
      runDataLoadingQueue: action,
      getMediaData: action,
      getGroupsWithPerson: action,
      addPerson: action,
      createPerson: action,
      addTemplateToPerson: action,
      createTemplate: action,
      addNewPerson: action,
      updatePerson: action,
      deletePerson: action,
      _openedPersonId: observable,
      getAllPersons: action,
      getItems: override,
      setPersonMediaSrc: action,
      _inputPersonIdPart: observable,
      _excludeGroupId: observable,
      selectedPersons: observable
    })

    this.rootStore = rootStore
  }

  size = 10
  _persons = {}

  get persons () {
    return this.rootStore.personsStore._persons
  }

  set persons (value) {
    this.rootStore.personsStore._persons = value
  }

  _dataLoadingQueue = []

  get dataLoadingQueue () {
    return this._dataLoadingQueue
  }

  set dataLoadingQueue (value) {
    this._dataLoadingQueue = value
  }

  // TODO data types const export
  async loadPersonData (personId, modality) {
    const person = this.person(personId).get()
    await this.addPersonToDataLoadingQueue(person, modality)
  }

  async addPersonToDataLoadingQueue (person, modality) {
    this.dataLoadingQueue.push([person, modality])
    if (this.dataLoadingQueue.length === 1) {
      await this.runDataLoadingQueue()
    }
  }

  async runDataLoadingQueue () {
    try {
      const [person, modality] = this.dataLoadingQueue[0]
      if (person[modality.dataName] === undefined) {
        person[modality.dataName] = await this.getMediaData(person.id, modality)
      }
      this.dataLoadingQueue.shift()
      if (this.dataLoadingQueue.length) {
        await this.runDataLoadingQueue()
      }
    } catch (e) {
      this.rootStore.messagesStore.setMessage({ ...dynamicMessages.media_load_fail, info: e })
    }
  }

  async getMediaData (personId, modality) {
    const filter = {
      metadata: [
        {
          path: 'external_id',
          comparison: {
            operand: OPERANDS.AND,
            not: false,
            comparison: COMPARISONS.EQUAL.name,
            value1: personId
          }
        }, {
          path: 'domain_id',
          comparison: {
            operand: OPERANDS.AND,
            not: false,
            comparison: COMPARISONS.EQUAL.name,
            value1: this.rootStore.domainStore.domainId
          }
        }],
      page: 1,
      size: 1,
      file_type: [{
        operand: OPERANDS.AND,
        not: false,
        comparison: COMPARISONS.EQUAL.name,
        value1: modality.dataType
      }]
    }
    try {
      const response = await MEDIASTORAGE_API.getFilesByParameters(getURL(URL_TYPES.MEDIASTORAGE), filter, this.rootStore.sessionStore.sessionId)
      return response.data.files.length ? response.data.files[0].file : null
    } catch (e) {
      if (e.response && e.response.data.reason === 'NOT_ENOUGH_MEMORY') {
        return null
      }
      throw e
    }
  }

  async getGroupsWithPerson (personId) {
    const person = this.person(personId).get()
    try {
      const response = await GROUP_API.getGroupsWithPerson(getURL(URL_TYPES.GROUP), personId, this.rootStore.sessionStore.sessionId)
      person.groupIds = response.data.map(group => group.id)
    } catch (e) {
      person.groupIds = []
      this.rootStore.messagesStore.setMessage({ ...dynamicMessages.profile_load_groups_fail, info: e })
    }
  }

  personGroups (personGroupIds) {
    return computed(() => this.rootStore.groupsStore.groups
      ? this.rootStore.groupsStore.groups.filter(group => personGroupIds.includes(group.id))
      : [])
  }

  addPerson (newPerson) {
    this.persons[newPerson.id] = (newPerson)
  }

  async createPerson (newPerson) {
    const personId = newPerson.id || generateUID()
    let isError = false
    for (const modality of Object.values(MODALITIES)) {
      const dataName = modality.dataName
      if (newPerson[dataName]) {
        try {
          await this.addTemplateToPerson(modality, personId, newPerson[dataName], true)
          if (!this.openedPersonId) {
            this.openedPersonId = personId
            this.rootStore.messagesStore.setMessage(dynamicMessages.profile_create_success, SEVERITIES.SUCCESS)
          }
        } catch (e) {
          isError = true
          this.rootStore.messagesStore.setMessage({ ...dynamicMessages.profile_create_template_fail, info: e })
        }
      }
    }
    await this.getItems()
    if (isError) throw new Error()
  }

  async addTemplateToPerson (modality, personId, base64Data, throwError) {
    await this.createTemplate(modality, personId, base64Data, throwError)
    let person = this.person(personId).get()
    if (!person) {
      this.addNewPerson(personId)
      person = this.person(personId).get()
    }
    person[modality.dataName] = base64Data
  }

  async createTemplate (modality, personId, base64Data, throwError) {
    try {
      const data = {
        person_data: [
          {
            person_id: personId,
            data: base64Data
          }
        ]
      }
      await TRACKER_API.createTemplate(url, data, modality, this.rootStore.sessionStore.sessionId)
    } catch (e) {
      const error = this.getPersonError(e)
      if (throwError) throw error
      this.rootStore.messagesStore.setMessage({ ...dynamicMessages.profile_create_template_fail, info: error })
    }
  }

  getPersonError (e) {
    return (e.response && e.response.status === 409) ? e.response.data.person_statuses[0].status : e
  }

  addNewPerson (personId) {
    const newPerson = {
      id: personId,
      groupIds: [],
      audio: null,
      photo: null
    }
    this.addPerson(newPerson)
  }

  async updatePerson (newPerson, oldPerson) {
    let isError = false
    for (const modality of Object.values(MODALITIES)) {
      const dataName = modality.dataName
      if (newPerson[dataName] && oldPerson[dataName] !== newPerson[dataName]) {
        try {
          await this.addTemplateToPerson(modality, newPerson.id, newPerson[dataName], true)
          this.rootStore.messagesStore.setMessage(dynamicMessages.template_create_success, SEVERITIES.SUCCESS)
        } catch (e) {
          this.rootStore.messagesStore.setMessage({ ...dynamicMessages.profile_create_template_fail, info: e })
          isError = true
        }
      }
    }
    if (isError) throw new Error()
  }

  async deletePerson (personId) {
    try {
      await TRACKER_API.deletePersons(url, { person_ids: [personId] }, this.rootStore.sessionStore.sessionId)
      await this.updateItemsAfterDelete()
      this.persons[personId] = null
      this.rootStore.messagesStore.setMessage(dynamicMessages.profile_delete_success, SEVERITIES.SUCCESS)
    } catch (e) {
      this.rootStore.messagesStore.setMessage({
        ...dynamicMessages.profile_delete_fail,
        info: this.getPersonError(e)
      })
    }
  }

  async updateItemsAfterDelete () {
    const availablePagesCount = Math.ceil((this.total - 1) / this.size) || 1
    if (this.page >= availablePagesCount) {
      this.page = availablePagesCount
    }
    await this.getItems()
  }

  person (personId) {
    return computed(
      () => this.persons[personId]
    )
  }

  _openedPersonId = null

  get openedPersonId () {
    return this._openedPersonId
  }

  set openedPersonId (value) {
    this._openedPersonId = value

    if (value) {
      const person = this.person(value).get()
      this.getGroupsWithPerson(value)
      if (person.audio === undefined) {
        this.loadPersonData(value, MODALITIES.VOICE)
      }
    }
  }

  async getAllPersons () {
    try {
      const response = await GROUP_API.getPersons(getURL(URL_TYPES.GROUP), this.rootStore.sessionStore.sessionId)
      this.persons = response.data.map(person => ({
        id: person.external_id
      }))
    } catch (e) {
      // persons partly could be added by PersonCard
      if (!this.persons) {
        this.persons = []
      }
      this.rootStore.messagesStore.setMessage({ ...dynamicMessages.profile_get_all_fail, info: e })
    }
  }

  async getItems () {
    try {
      this.filters.external_id = this._inputPersonIdPart
      this.filters.exclude_group = this._excludeGroupId
      const filledFilterFields = this.getFilledFilterFields()
      const response = await GROUP_API.getPersonsPage(
        getURL(URL_TYPES.GROUP),
        this.getQueryParams(filledFilterFields),
        this.rootStore.sessionStore.sessionId
      )
      await this.setItems(response.data, { itemReducers: this.reducer })
    } catch (e) {
      this.items = []
      this.rootStore.messagesStore.setMessage({ ...dynamicMessages.profile_get_all_fail, info: e })
    }
  }

  reducer = (person) => {
    const per = { id: person.external_id }
    if (!this.persons[per.id]) {
      this.addPerson(per)
    }
    return per
  }

  setPersonMediaSrc (modality, personId, fileSrc) {
    let person = this.person(personId).get()
    if (!person) {
      person = { id: personId }
      this.persons[personId] = person
    }
    person[modality.mediaSrcName] = fileSrc
  }

  _inputPersonIdPart = ''

  get inputPersonIdPart () {
    return this._inputPersonIdPart
  }

  set inputPersonIdPart (value) {
    this._inputPersonIdPart = value
    this.getItems()
  }

  setInputPersonIdPart (value) {
    this._inputPersonIdPart = value
    this.getItems()
  }

  resetInput () {
    this.page = defaultPage
    this._inputPersonIdPart = ''
    this.getItems()
  }

  _excludeGroupId = ''

  get excludeGroupId () {
    return this._excludeGroupId
  }

  set excludeGroupId (value) {
    this._excludeGroupId = value
  }

  selectedPersons (items) {
    const ids = items.map(t => t.id)
    return items ? Object.keys(this.persons)
      .filter(p => ids.includes(p))
      .map(key => this.persons[key])
      : []
  }
}
